import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Phone,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  User,
  UserPlus,
} from 'lucide-react'
import { useSession } from '../state/session'
import { useTranslation } from '../i18n/LanguageContext'
import { LanguageSelector } from '../components/LanguageSelector'

interface LocationState {
  from?: string
  message?: string
}

type AuthMode = 'password' | 'register' | 'forgot' | 'otp'

export function SignIn() {
  const {
    signedIn,
    requestCode,
    verifyCode,
    loginWithPassword,
    register,
    requestResetCode,
    resetPassword,
    busy,
    error,
    clearError,
  } = useSession()

  const { isHindi } = useTranslation()

  const [mode, setMode] = useState<AuthMode>('password')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [code, setCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState<'input' | 'otp'>('input')
  const [countdown, setCountdown] = useState(30)
  const [canResend, setCanResend] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state as LocationState) || {}
  const targetDestination = state.from || '/'

  // Redirect if already signed in
  useEffect(() => {
    if (signedIn) {
      navigate(targetDestination, { replace: true })
    }
  }, [signedIn, navigate, targetDestination])

  // Countdown timer for OTP resend
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (step === 'otp' && countdown > 0) {
      timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    } else if (step === 'otp' && countdown === 0) {
      setCanResend(true)
    }
    return () => clearTimeout(timer)
  }, [step, countdown])

  function switchMode(newMode: AuthMode) {
    setMode(newMode)
    setStep('input')
    setCode('')
    setPassword('')
    setConfirmPassword('')
    setValidationError(null)
    clearError()
  }

  function handlePhoneChange(val: string) {
    const raw = val.replace(/\D/g, '')
    setPhone(raw.length > 0 ? `+91${raw}` : '')
    setValidationError(null)
    clearError()
  }

  const phoneDigits = phone.startsWith('+91') ? phone.slice(3).trim() : phone

  // 1. Password Login (Zero OTP!)
  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    setValidationError(null)

    if (phoneDigits.length < 10) {
      setValidationError('Please enter a valid 10-digit mobile number.')
      return
    }
    if (!password) {
      // Seamless fallback: if submitted without password, switch to OTP sign-in
      try {
        await requestCode(phone)
        setMode('otp')
        setStep('otp')
        setCountdown(30)
        setCanResend(false)
      } catch {
        // Handled in session
      }
      return
    }

    try {
      await loginWithPassword(phone, password)
      navigate(targetDestination, { replace: true })
    } catch {
      // Error handled by useSession
    }
  }

  // 2. Request OTP for Registration or Password Reset or OTP Login
  async function handleRequestOtp() {
    clearError()
    setValidationError(null)

    if (phoneDigits.length < 10) {
      setValidationError('Please enter a valid 10-digit mobile number.')
      return
    }

    if (mode === 'register') {
      if (password.length < 6) {
        setValidationError('Password must be at least 6 characters long.')
        return
      }
      if (password !== confirmPassword) {
        setValidationError('Passwords do not match.')
        return
      }
    }

    try {
      if (mode === 'forgot') {
        await requestResetCode(phone)
      } else {
        await requestCode(phone)
      }
      setStep('otp')
      setCountdown(30)
      setCanResend(false)
    } catch {
      // Error handled by useSession
    }
  }

  // Resend OTP
  async function handleResendOtp() {
    if (!canResend || busy) return
    clearError()
    setValidationError(null)

    try {
      if (mode === 'forgot') {
        await requestResetCode(phone)
      } else {
        await requestCode(phone)
      }
      setCountdown(30)
      setCanResend(false)
    } catch {
      // Error handled by useSession
    }
  }

  // 3. Verify OTP & Complete Flow
  async function handleCompleteOtpFlow(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    setValidationError(null)

    if (code.length < 4) {
      setValidationError('Please enter the complete 4-digit OTP code.')
      return
    }

    try {
      if (mode === 'register') {
        await register(phone, code, password, fullName.trim() || undefined)
      } else if (mode === 'forgot') {
        if (password.length < 6) {
          setValidationError('New password must be at least 6 characters long.')
          return
        }
        if (password !== confirmPassword) {
          setValidationError('Passwords do not match.')
          return
        }
        await resetPassword(phone, code, password)
      } else {
        // Fallback OTP login
        await verifyCode(phone, code)
      }
      navigate(targetDestination, { replace: true })
    } catch {
      // Error handled by useSession
    }
  }

  const activeError = validationError || error

  return (
    <div className="relative mx-auto my-2 max-w-md px-1 sm:px-2 sm:my-10 overflow-hidden">
      {/* Decorative gradient blur backdrop */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-tr from-emerald-100/60 via-teal-50/40 to-amber-50/50 blur-xl" />

      {/* Language Switcher on Sign In Screen */}
      <div className="mb-3 flex justify-end">
        <LanguageSelector variant="compact" />
      </div>

      <div className="card relative overflow-hidden border-stone-200/90 bg-white/95 p-4 sm:p-8 shadow-xl shadow-stone-200/50 backdrop-blur-sm">
        {/* Header / Brand */}
        <div className="mb-6 text-center">
          <img
            src="/rojanamartlogo.png"
            alt="RojanaMart"
            className="mx-auto mb-3 h-16 w-16 sm:h-20 sm:w-20 rounded-2xl object-contain shadow-md border border-stone-100 bg-white"
          />
          <h1 className="text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">
            {mode === 'password' && (isHindi ? 'वापसी पर स्वागत है' : 'Welcome Back')}
            {mode === 'register' && (step === 'otp' ? (isHindi ? 'मोबाइल सत्यापित करें और रजिस्टर करें' : 'Verify Mobile & Register') : (isHindi ? 'नया खाता बनाएं' : 'Create an Account'))}
            {mode === 'forgot' && (step === 'otp' ? (isHindi ? 'नया पासवर्ड सेट करें' : 'Set New Password') : (isHindi ? 'पासवर्ड रीसेट करें' : 'Reset Your Password'))}
            {mode === 'otp' && (step === 'otp' ? (isHindi ? 'OTP कोड दर्ज करें' : 'Enter One-Time Code') : (isHindi ? 'OTP से साइन इन करें' : 'Sign In with OTP'))}
          </h1>
          <p className="mt-1.5 text-sm text-stone-600">
            {mode === 'password' && (isHindi ? 'साइन इन करने के लिए अपना मोबाइल नंबर और पासवर्ड दर्ज करें।' : 'Enter your mobile number and password to sign in.')}
            {mode === 'register' &&
              (step === 'otp'
                ? (isHindi ? `+91 ${phoneDigits} पर WhatsApp पर भेजा गया 4 अंकों का OTP दर्ज करें` : `Enter the 4-digit OTP sent via WhatsApp to +91 ${phoneDigits}`)
                : (isHindi ? 'आसान और त्वरित ग्रॉसरी शॉपिंग के लिए मोबाइल और पासवर्ड से जुड़ें।' : 'Sign up once with mobile & password for seamless shopping.'))}
            {mode === 'forgot' &&
              (step === 'otp'
                ? (isHindi ? `+91 ${phoneDigits} के लिए नया पासवर्ड चुनें` : `Enter OTP and choose a new password for +91 ${phoneDigits}`)
                : (isHindi ? 'नया पासवर्ड सेट करने से पहले हम आपके WhatsApp पर एक OTP भेजेंगे।' : "We'll send an OTP via WhatsApp to verify phone ownership before setting a new password."))}
            {mode === 'otp' &&
              (step === 'otp'
                ? (isHindi ? `हमने +91 ${phoneDigits} पर WhatsApp द्वारा 4 अंकों का OTP भेजा है` : `We sent a 4-digit OTP code via WhatsApp to +91 ${phoneDigits}`)
                : (isHindi ? 'बिना पासवर्ड के WhatsApp कोड द्वारा त्वरित लॉगिन करें।' : 'Quick sign-in without password using WhatsApp verification code.'))}
          </p>
          {state.message && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              {state.message}
            </div>
          )}
        </div>

        {/* Mode Navigation Tabs (Password vs Register) */}
        {step === 'input' && (
          <div className="mb-6 flex rounded-xl bg-stone-100 p-1 text-sm font-medium">
            <button
              type="button"
              onClick={() => switchMode('password')}
              className={`flex-1 rounded-lg py-2 text-center transition-all ${
                mode === 'password'
                  ? 'bg-white font-semibold text-stone-900 shadow-sm'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {isHindi ? 'साइन इन' : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={() => switchMode('register')}
              className={`flex-1 rounded-lg py-2 text-center transition-all ${
                mode === 'register'
                  ? 'bg-white font-semibold text-emerald-800 shadow-sm'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {isHindi ? 'नया खाता (रजिस्टर)' : 'Register (New)'}
            </button>
          </div>
        )}

        {/* Error notification */}
        {activeError && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-2.5 rounded-xl border border-red-200/80 bg-red-50/90 p-3.5 text-sm text-red-800"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div className="flex-1 text-xs font-medium leading-relaxed">{activeError}</div>
            <button
              type="button"
              onClick={() => {
                clearError()
                setValidationError(null)
              }}
              className="text-xs font-bold text-red-700 hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* ============================================================== */}
        {/* VIEW 1: PASSWORD LOGIN (Zero OTP - Fastest & Zero SMS Cost)    */}
        {/* ============================================================== */}
        {mode === 'password' && (
          <form className="space-y-4" onSubmit={handlePasswordLogin}>
            <div>
              <label htmlFor="phone-input" className="label flex items-center justify-between">
                <span>Mobile Phone Number</span>
                <span className="text-[11px] font-normal lowercase tracking-normal text-stone-400">
                  India (+91)
                </span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-500">
                  <Phone className="h-4 w-4 text-stone-400" />
                  <span className="ml-2 font-medium text-stone-600">+91</span>
                  <div className="mx-2 h-4 w-px bg-stone-300" />
                </div>
                <input
                  id="phone-input"
                  className="field pl-20 font-medium"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="98765 43210"
                  required
                  value={phoneDigits}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="login-password" className="label">
                  Password <span className="text-[11px] font-normal text-stone-400">(Leave blank for OTP sign-in)</span>
                </label>
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-xs font-semibold text-emerald-700 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-400">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  id="login-password"
                  className="field pl-10 pr-10"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Developer / Customer Quick Fill */}
              <div className="rounded-lg border border-stone-100 bg-stone-50/80 p-2 text-xs">
                <span className="font-medium text-stone-600">Quick Fill Account:</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      handlePhoneChange('8787247736')
                      setPassword('Pkm@763421')
                    }}
                    className="rounded border border-emerald-300 bg-emerald-50/80 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 transition-colors"
                  >
                    8787247736 (Pkm@763421)
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary w-full py-3 text-base shadow-lg shadow-emerald-700/20"
              disabled={busy || phoneDigits.length < 10}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Please wait…
                </span>
              ) : password ? (
                <span className="inline-flex items-center gap-1.5">
                  Sign In with Password <ArrowRight className="h-4 w-4" />
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  Get Verification Code <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </button>

            <div className="flex items-center justify-between border-t border-stone-100 pt-3 text-xs">
              <span className="text-stone-500">Don't have a password yet?</span>
              <button
                type="button"
                onClick={() => switchMode('otp')}
                className="font-semibold text-emerald-700 hover:underline"
              >
                Sign in with OTP instead
              </button>
            </div>
          </form>
        )}

        {/* ============================================================== */}
        {/* VIEW 2: REGISTER (Phone + Name + Password -> 1 OTP -> Account) */}
        {/* ============================================================== */}
        {mode === 'register' && step === 'input' && (
          <form
            className="space-y-3.5"
            onSubmit={(e) => {
              e.preventDefault()
              void handleRequestOtp()
            }}
          >
            <div>
              <label htmlFor="reg-name" className="label">
                Full Name <span className="text-stone-400 font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-400">
                  <User className="h-4 w-4" />
                </div>
                <input
                  id="reg-name"
                  className="field pl-10"
                  type="text"
                  autoComplete="name"
                  placeholder="e.g. Rahul Sharma"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="reg-phone" className="label flex items-center justify-between">
                <span>Mobile Phone Number</span>
                <span className="text-[11px] font-normal lowercase tracking-normal text-stone-400">
                  India (+91)
                </span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-500">
                  <Phone className="h-4 w-4 text-stone-400" />
                  <span className="ml-2 font-medium text-stone-600">+91</span>
                  <div className="mx-2 h-4 w-px bg-stone-300" />
                </div>
                <input
                  id="reg-phone"
                  className="field pl-20 font-medium"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="98765 43210"
                  required
                  value={phoneDigits}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="reg-password" className="label">
                Create Password (Min 6 characters)
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-400">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  id="reg-password"
                  className="field pl-10 pr-10"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="reg-confirm-password" className="label">
                Confirm Password
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-400">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  id="reg-confirm-password"
                  className="field pl-10"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Re-enter password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            <p className="text-xs text-stone-500">
              We'll send a 1-time verification OTP to confirm your number. You will log in with your mobile & password in the future.
            </p>

            <button
              type="submit"
              className="btn-primary w-full py-3 text-base shadow-lg shadow-emerald-700/20"
              disabled={busy || phoneDigits.length < 10 || password.length < 6 || password !== confirmPassword}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Sending OTP…
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <UserPlus className="h-4 w-4" /> Verify OTP & Create Account
                </span>
              )}
            </button>
          </form>
        )}

        {/* ============================================================== */}
        {/* VIEW 3: FORGOT PASSWORD (Enter Phone -> Request Reset OTP)     */}
        {/* ============================================================== */}
        {mode === 'forgot' && step === 'input' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              void handleRequestOtp()
            }}
          >
            <div>
              <label htmlFor="forgot-phone" className="label flex items-center justify-between">
                <span>Enter Your Registered Mobile</span>
                <span className="text-[11px] font-normal lowercase tracking-normal text-stone-400">
                  India (+91)
                </span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-500">
                  <Phone className="h-4 w-4 text-stone-400" />
                  <span className="ml-2 font-medium text-stone-600">+91</span>
                  <div className="mx-2 h-4 w-px bg-stone-300" />
                </div>
                <input
                  id="forgot-phone"
                  className="field pl-20 font-medium"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="98765 43210"
                  required
                  value={phoneDigits}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  autoFocus
                />
              </div>
              <p className="mt-1.5 text-xs text-stone-500">
                {isHindi
                  ? 'हम आपके WhatsApp पर 4 अंकों का OTP कोड भेजेंगे जिससे आप नया पासवर्ड सेट कर सकेंगे।'
                  : 'We will send a 4-digit OTP code via WhatsApp to verify your account and allow you to set a new password.'}
              </p>
            </div>

            <button
              type="submit"
              className="btn-primary w-full py-3 text-base shadow-lg shadow-emerald-700/20"
              disabled={busy || phoneDigits.length < 10}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Sending code…
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  Send Reset Code <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => switchMode('password')}
              className="w-full text-center text-xs font-semibold text-stone-600 hover:text-stone-900"
            >
              ← Back to Password Login
            </button>
          </form>
        )}

        {/* ============================================================== */}
        {/* VIEW 4: OTP SIGN IN (Instant fallback without password)        */}
        {/* ============================================================== */}
        {mode === 'otp' && step === 'input' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              void handleRequestOtp()
            }}
          >
            <div>
              <label htmlFor="otp-phone" className="label flex items-center justify-between">
                <span>Mobile Phone Number</span>
                <span className="text-[11px] font-normal lowercase tracking-normal text-stone-400">
                  India (+91)
                </span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-500">
                  <Phone className="h-4 w-4 text-stone-400" />
                  <span className="ml-2 font-medium text-stone-600">+91</span>
                  <div className="mx-2 h-4 w-px bg-stone-300" />
                </div>
                <input
                  id="otp-phone"
                  className="field pl-20 font-medium"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="98765 43210"
                  required
                  value={phoneDigits}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  autoFocus
                />
              </div>
              <p className="mt-1.5 text-xs text-stone-500">
                We'll send an SMS with a one-time verification code.
              </p>
            </div>

            <button
              type="submit"
              className="btn-primary w-full py-3 text-base shadow-lg shadow-emerald-700/20"
              disabled={busy || phoneDigits.length < 10}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Please wait…
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  Get Verification Code <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => switchMode('password')}
              className="w-full text-center text-xs font-semibold text-emerald-700 hover:underline"
            >
              ← Back to Sign In with Password
            </button>
          </form>
        )}

        {/* ============================================================== */}
        {/* OTP VERIFICATION STEP (Used for Register, Forgot, & OTP login) */}
        {/* ============================================================== */}
        {step === 'otp' && (
          <form className="space-y-4" onSubmit={handleCompleteOtpFlow}>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="otp-input" className="label">
                  <span>4-Digit Verification Code (WhatsApp)</span>
                </label>
                <button
                  type="button"
                  className="text-xs font-semibold text-emerald-700 hover:underline"
                  onClick={() => {
                    setStep('input')
                    setCode('')
                    clearError()
                    setValidationError(null)
                  }}
                >
                  Edit number
                </button>
              </div>

              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-400">
                  <KeyRound className="h-4 w-4" />
                </div>
                <input
                  id="otp-input"
                  className="field pl-10 text-center tracking-[0.3em] font-bold text-lg text-emerald-900"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={4}
                  placeholder="••••"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                />
              </div>

              <div className="mt-2 flex items-center justify-between text-xs text-stone-500">
                <span>Didn't receive on WhatsApp?</span>
                {canResend ? (
                  <button
                    type="button"
                    onClick={() => void handleResendOtp()}
                    disabled={busy}
                    className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
                  >
                    <RotateCcw className="h-3 w-3" /> Resend OTP
                  </button>
                ) : (
                  <span className="tabular-nums text-stone-400">Resend in {countdown}s</span>
                )}
              </div>
            </div>

            {/* In Forgot Password Mode, also show new password inputs on OTP step */}
            {mode === 'forgot' && (
              <div className="space-y-3 border-t border-stone-100 pt-3">
                <div>
                  <label htmlFor="new-password" className="label">
                    New Password (Min 6 characters)
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-400">
                      <Lock className="h-4 w-4" />
                    </div>
                    <input
                      id="new-password"
                      className="field pl-10 pr-10"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-new-password" className="label">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-stone-400">
                      <Lock className="h-4 w-4" />
                    </div>
                    <input
                      id="confirm-new-password"
                      className="field pl-10"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Re-enter new password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full py-3 text-base shadow-lg shadow-emerald-700/20"
              disabled={
                busy ||
                code.length < 4 ||
                (mode === 'forgot' && (password.length < 6 || password !== confirmPassword))
              }
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Verifying…
                </span>
              ) : mode === 'register' ? (
                <span className="inline-flex items-center gap-1.5">
                  Complete Registration & Sign In <ArrowRight className="h-4 w-4" />
                </span>
              ) : mode === 'forgot' ? (
                <span className="inline-flex items-center gap-1.5">
                  Update Password & Sign In <ArrowRight className="h-4 w-4" />
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  Verify & Continue <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => switchMode('password')}
              className="w-full text-center text-xs text-stone-500 hover:text-stone-700"
            >
              ← Cancel and return to Password Login
            </button>
          </form>
        )}

        {/* Local development hint */}
        <div className="mt-6 rounded-xl border border-stone-200/80 bg-stone-50/70 p-3 text-xs text-stone-500">
          <p className="flex items-center gap-1.5 font-medium text-stone-700">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            Zero-OTP Architecture
          </p>
          <p className="mt-1">
            Daily login uses <strong>Mobile + Password</strong> with <strong>0 OTPs</strong> to minimize SMS expenses.
            OTP is only required once during sign-up or if password is forgotten. In local dev, codes appear as{' '}
            <code className="rounded bg-stone-200 px-1 py-0.5 font-mono font-semibold text-emerald-800">DEVELOPMENT OTP</code> in backend logs.
          </p>
        </div>

        {/* Security Trust Badges */}
        <div className="mt-6 flex items-center justify-center gap-4 border-t border-stone-100 pt-4 text-xs text-stone-500">
          <div className="flex items-center gap-1">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <span>BCrypt 256-bit Secure</span>
          </div>
          <div className="h-3 w-px bg-stone-200" />
          <div className="flex items-center gap-1">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span>Zero OTP Login</span>
          </div>
        </div>
      </div>
    </div>
  )
}
